import React, { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ConfigProvider, notification } from 'antd';
import { useAntdLocale } from '../hooks/use-antd-locale';

import './app.scss';
import { loggedInRouter } from '../routers/logged-in';
import { loggedOutRouter } from '../routers/logged-out';
import { useStore } from '../helpers/use-store';
import { observer } from 'mobx-react-lite';
import {
  LoadingState,
  useLoading,
  useResponsiveness,
  UseResponsivenessProps,
  useThemeToken,
} from '@monorepo/react-components';
import { NotificationProvider } from '../hooks/use-notification';
import { ResponsiveProvider } from '../hooks/use-responive-ready';
import Clarity from '@microsoft/clarity';

declare global {
  interface Window {
    Featurebase: any;
  }
}

const App = () => {
  const [notificationApi, contextHolder] = notification.useNotification();
  const { loadingState, updateLoadingState } = useLoading(LoadingState.Loading);
  const [responsiveReady, setResponsiveReady] = useState(false);
  const antdLocale = useAntdLocale();
  const responsivenessValues = useResponsiveness();
  const {
    dataStore: { userStore },
  } = useStore();
  const { theme } = useThemeToken();

  useEffect(() => {
    const init = async () => {
      try {
        await userStore.login();
      } catch (e) {
        console.error(`failed login`, e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (responsivenessValues.isMobile !== undefined) {
      setResponsiveReady(true);
    }
  }, [responsivenessValues.isMobile]);

  useEffect(() => {
    if (userStore.isLoggedIn) {
      Clarity.setTag('email', userStore.currentUser.email);
    }
  }, [userStore.isLoggedIn]);

  useEffect(() => {
    if (!userStore.isLoggedIn) {
      return;
    }

    userStore
      .fetchFeaturebaseToken()
      .then((featurebaseJwt) => {
        window.Featurebase('boot', {
          appId: '6773dfc17e80c86d665aa811',
          featurebaseJwt,
          theme: 'light',
          language: 'en',
        });

        window.Featurebase('init_changelog_widget', {
          organization: 'shortsninja',
          popup: {
            enabled: true,
            usersName: userStore.currentUser.firstName || 'Creator',
            autoOpenForNewUpdates: true,
          },
          theme: 'light',
          locale: 'en',
        });
      })
      .catch(() => {
        window.Featurebase('boot', {
          appId: '6773dfc17e80c86d665aa811',
          theme: 'light',
          language: 'en',
        });
      });
  }, [userStore.isLoggedIn]);

  const router = userStore.isLoggedIn ? loggedInRouter : loggedOutRouter;

  if (loadingState === LoadingState.Loading || !responsiveReady) {
    return null;
  }

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{ cssVar: true, algorithm: theme.defaultAlgorithm }}
    >
      <ResponsiveProvider
        value={responsivenessValues as UseResponsivenessProps}
      >
        <NotificationProvider value={notificationApi}>
          <RouterProvider router={router} />
          {contextHolder}
        </NotificationProvider>
      </ResponsiveProvider>
    </ConfigProvider>
  );
};

export default observer(App);
